import { VidParamsV3Clip } from '@sportlogiq/shared/types';
import { Game, Video } from './game.models';
import {
    reduce as _reduce,
    isEmpty as _isEmpty,
    sum as _sum,
    flatMap as _flatMap,
    values as _values
} from 'lodash';
import { GameAPI, GameModelAPIV3, GameScoreAPI, GamesRequestResponseV3, GameStateAPI, GameStateModel, PeriodScore } from './game.types';
import { Team } from '../team/team.models';
import { Constants } from 'environments/constants';

/**
 * @deprecated
 */
export function generateLeagueSeasonKey(leagueId: string, seasonId: string, seasonStage: string): string {
    return `${leagueId}-${seasonId}-${seasonStage}`;
}

export const isGameQaComplete = (gameState: GameStateAPI): boolean => {
    return gameState.homeTeamPlayerEventsComplete && gameState.awayTeamPlayerEventsComplete;
};

export const gameStateDefaults: GameStateModel = {
    games: {},
    gamesByGameId: {},
    vidParamsByGameId: {},
    statesByGameId: {},
    scoresByGameId: {},
    loading: false,
    gamePlays: {},
};

export const isTeamId = (homeTeamId: string, awayTeamId: string, teamId: string): boolean => {
    return homeTeamId === teamId || awayTeamId === teamId;
};

export const convertPeriodScoreToV2 = (periodScore: PeriodScore): Record<string, number[]> | null => {
    if (periodScore === null) {
        return null;
    }

    return _reduce(periodScore, (acc, value) => {
        Object.keys(value).forEach((teamKey) => {
            if (_isEmpty(acc[teamKey])) {
                acc[teamKey] = [];
            }
            acc[teamKey].push(value[teamKey]);
        });

        return acc;
    }, {} as Record<string, number[]>);
};

export const convertVidparamsV3ToVideoV2 = (vidParams:  VidParamsV3Clip[]): Video[] => {
    if (vidParams === null) {
        return [];
    }

    return vidParams.map((vidParam:  VidParamsV3Clip) => {
        return {
            id: vidParam.vidId,
            frameRate: vidParam.frameRate,
            width: vidParam.width,
            height: vidParam.height,
            fileName: vidParam.filename,
            rootUrl: vidParam.rootUrl,
            period: vidParam.period,
            periodType: vidParam.periodType,
        };
    });
};

/**
 * A ScoutIQ game is considered valid when the metrics are not fully processed (i.e., metricsFullProcessed is false)
 * and the isScoutIq flag from the season stage is true.
 * By default, the isScoutIq flag in the season stage will mark all games as ScoutIQ until the metrics are fully processed
 * (i.e., metricsFullProcessed becomes true).
 *
 * @param metricsFullyProcessed: boolean
 * @param isScoutIqSeason: boolean
 * @returns boolean value
 */
export const isScoutIqGame = (metricsFullyProcessed: boolean, isScoutIqSeason: boolean): boolean => {
    return !metricsFullyProcessed && isScoutIqSeason;
};

/**
 * Transforms V3 games into the deprecated Game class
 * @param gamesRequestResponseV3
 * @param teamsById
 */
export const convertGamesV3ToGameV2 = (gamesRequestResponseV3:  GamesRequestResponseV3, teamsById: Record<string, Team>): Game[] => {
    return convertGameV3ToGameAPIV2(gamesRequestResponseV3).map((game) => {
        const teams = {};
        teams[game.homeTeamId] = teamsById[game.homeTeamId];
        teams[game.awayTeamId] = teamsById[game.awayTeamId];

        return new Game(game, teams);
    });
};

/**
 * Exmaple: { 1: true, 2: false, 3: true };
 * Will return 3 since the max true value is on the 3rd index which will the current live period.
 *
 * @param periodHasGameEvents
 * @returns number
 */
export const getCurrentLivePeriodFromPeriodHasEvents = (periodHasGameEvents: Record<number, boolean>): number => {
    const hasNoEventsWithinPeriods = Object.keys(periodHasGameEvents).every(key => !periodHasGameEvents[key]);

    if (hasNoEventsWithinPeriods) {
        return 0;
    }

    return Math.max(
        ...Object.keys(periodHasGameEvents)
            .filter(key => periodHasGameEvents[key]) // only keep keys with `true` values
            .map(Number)
    );
};

/**
 * Takes V3 games and transforms them into the deprecated GameAPI model
 * @param gamesRequestResponseV3
 * @returns
 */
export const convertGameV3ToGameAPIV2 = (gamesRequestResponseV3:  GamesRequestResponseV3): GameAPI[] => {
    const { games, scores, states, vidparams }  = gamesRequestResponseV3;

    if (states === null || scores === null || vidparams === null) {
        console.warn('Trying to convert V3 games to V2 without necessary information');
    }

    return games.map((game: GameModelAPIV3) => {
        const gameState = states?.[game.id];
        const scoreGame = scores?.[game.id];
        const gameVideos = vidparams?.[game.id];

        return {
            id: game.id,
            season: { id: game.seasonId, stage: game.seasonStage },
            date: game.scheduledTime,
            scheduledTime: game.scheduledTime,
            scheduledVenueTime: game.scheduledVenueTime,
            venueId: game.venueId,
            referenceId: game.referenceId,
            sequenceId: parseInt(gameState?.sequenceId, 10),
            periods: gameState.isInProgress ? getCurrentLivePeriodFromPeriodHasEvents(gameState.periodHasGameEvents) : game.periodCount,
            inProgress: gameState?.isInProgress,
            isCompleted: gameState?.metricsFullyProcessed,
            metricsFullyProcessed: gameState?.metricsFullyProcessed ? '1' : '0',
            isPostponed: gameState?.isPostponed,
            isLocked: gameState?.isLocked,
            isFirstDataEntryComplete: gameState?.firstDataEntryCompleteTime !== null,
            score: scoreGame?.score,
            hasShootout: scoreGame?.shootoutScore !== null,
            periodScore: convertPeriodScoreToV2(scoreGame?.periodScore),
            awayTeamId: game.awayTeamId,
            homeTeamId: game.homeTeamId,
            isUpcoming: gameState?.isUpcoming,
            showAsUpcoming: gameState?.showAsUpcoming,
            isShiftsCompleted: gameState?.firstShiftsEntryCompleteTime !== null,
            videos: convertVidparamsV3ToVideoV2(gameVideos),
        };
    });
};

/**
 * Returns a boolean that is true if the game is considered to be 'in the live window'. Note that this does not mean that game is currently 'live' but that it could become live soon or was recently live.
 * @param gameDate
 * @param liveGamePastOffset
 * @param liveGameFutureOffset
 * @returns
 */
export const gameIsInLiveWindow = (gameDate: string | Date): boolean => {
    const nowTime = new Date();
    const nowTimePlusOffset = new Date(nowTime.setHours(nowTime.getHours() + Constants.liveGameFutureOffset));
    const nowTimeMinusOffset = new Date(nowTime.setHours(nowTime.getHours() - Constants.liveGamePastOffset));
    const dateToCheck = new Date(gameDate);
    return dateToCheck.getTime() >= nowTimeMinusOffset.getTime() && dateToCheck.getTime() <= nowTimePlusOffset.getTime();
};


/**
 * Returns the total number of goals for every periods of a game, excluding shootout.
 * If periodScore doesn't exist, the function will return 0
 * @param scores
 */
export const combinedGoalsForScore = (scores: GameScoreAPI): number => {
    return _sum(_flatMap(scores?.periodScore, (periodScores) => _values(periodScores)));
};
